import {
  ComHubQueryParamsEnum,
  ComHubTabKeys,
  DirectMessage,
  ChatModeEnum,
  Review,
  ReviewMetadata,
  UserActionAssociatedTypeEnum,
} from '@company/types/comm-hub.type';
import QueryKeys from '@constants/queryKeys';
import { IWorkerData } from '@th-types/worker.type';
import { IoMailOpenOutline, IoMailUnreadOutline } from 'react-icons/io5';

export const dismissItems = async (
  items: ReviewMetadata[],
  associatedType: UserActionAssociatedTypeEnum,
  mutateFunction: (itemsToDismiss: {
    items: Array<{
      associatedType: UserActionAssociatedTypeEnum;
      associatedIds: number[];
    }>;
  }) => Promise<void>
) => {
  const itemsToDismiss = {
    items: [
      {
        associatedType,
        associatedIds: items.map(
          (item) => (item as Review).userState?.associatedId ?? 0
        ),
      },
    ],
  };

  try {
    await mutateFunction(itemsToDismiss);
  } catch (error) {
    console.error('Error dismissing items:', error);
    // Re-throw the error so it can be handled by the mutation's onError callback
    throw error;
  }
};

export interface ComHubSearchParams {
  searchText: string;
  dateRange: { startDate: Date | undefined; endDate: Date | undefined };
}

export interface ComHubSearchOptions {
  itemsPerPage?: number;
  setCurrentOffset?: (offset: number) => void;
}

/**
 * Utility function to handle search in CommunicationHub components
 * @param search The search parameters
 * @param setSearchState Function to update the search state
 * @param updateSearchParams Function to update the URL search parameters
 * @param options Additional options like itemsPerPage and setCurrentOffset
 */
export const handleComHubSearch = (
  search: ComHubSearchParams,
  setSearchState: (search: ComHubSearchParams) => void,
  updateSearchParams: (params: URLSearchParams) => void,
  options: ComHubSearchOptions = {}
) => {
  const { itemsPerPage = 20, setCurrentOffset } = options;

  setSearchState(search);
  const newSearchParams = new URLSearchParams();

  if (search.searchText) {
    newSearchParams.set(ComHubQueryParamsEnum.SEARCH_TEXT, search.searchText);
  }
  if (search.dateRange.startDate) {
    newSearchParams.set(
      ComHubQueryParamsEnum.DATE_FROM,
      search.dateRange.startDate.toISOString()
    );
  }
  if (search.dateRange.endDate) {
    newSearchParams.set(
      ComHubQueryParamsEnum.DATE_TO,
      search.dateRange.endDate.toISOString()
    );
  }
  newSearchParams.set(ComHubQueryParamsEnum.OFFSET, '0');
  newSearchParams.set(ComHubQueryParamsEnum.MAX, itemsPerPage.toString());
  updateSearchParams(newSearchParams);

  if (setCurrentOffset) {
    setCurrentOffset(0);
  }
};

/**
 * Utility function to clear search in CommunicationHub components
 * @param setSearchState Function to update the search state
 * @param updateSearchParams Function to update the URL search parameters
 * @param options Additional options like itemsPerPage and setCurrentOffset
 */
export const handleComHubClear = (
  setSearchState: (search: ComHubSearchParams | undefined) => void,
  updateSearchParams: (params: URLSearchParams) => void,
  options: ComHubSearchOptions = {}
) => {
  const { itemsPerPage = 20, setCurrentOffset } = options;

  setSearchState(undefined);
  const newSearchParams = new URLSearchParams();
  newSearchParams.set(ComHubQueryParamsEnum.OFFSET, '0');
  newSearchParams.set(ComHubQueryParamsEnum.MAX, itemsPerPage.toString());
  updateSearchParams(newSearchParams);

  if (setCurrentOffset) {
    setCurrentOffset(0);
  }
};

/**
 * Interface for the result of extracting URL parameters
 */
export interface ComHubUrlParams {
  searchState?: ComHubSearchParams;
  showOnlyNew?: boolean;
  offset?: number;
}

/**
 * Utility function to extract URL parameters for CommunicationHub components
 * @param searchParams The URL search parameters
 * @returns An object containing the extracted parameters
 */
export const extractComHubUrlParams = (
  searchParams: URLSearchParams
): ComHubUrlParams => {
  const searchText = searchParams.get(ComHubQueryParamsEnum.SEARCH_TEXT);
  const dateFrom = searchParams.get(ComHubQueryParamsEnum.DATE_FROM);
  const dateTo = searchParams.get(ComHubQueryParamsEnum.DATE_TO);
  const showNew = searchParams.get(ComHubQueryParamsEnum.NEW);
  const showUnread = searchParams.get(ComHubQueryParamsEnum.UNREAD);
  const offset = searchParams.get(ComHubQueryParamsEnum.OFFSET);

  const result: ComHubUrlParams = {};

  if (searchText || dateFrom || dateTo) {
    result.searchState = {
      searchText: searchText || '',
      dateRange: {
        startDate: dateFrom ? new Date(dateFrom) : undefined,
        endDate: dateTo ? new Date(dateTo) : undefined,
      },
    };
  }

  // Check for either NEW or UNREAD parameter
  if (showNew !== null) {
    result.showOnlyNew = showNew === 'true';
  } else if (showUnread !== null) {
    result.showOnlyNew = showUnread === 'true';
  }

  if (offset !== null) {
    result.offset = Number(offset) || 0;
  }

  return result;
};

export interface QueryParamsOptions {
  page: number;
  itemsPerPage: number;
  searchText?: string;
  dateRange?: {
    startDate: Date | undefined;
    endDate: Date | undefined;
  };
  isNew?: boolean;
  isUnread?: boolean;
  mode?: ChatModeEnum | null;
}

export function buildComHubQueryParams({
  page,
  itemsPerPage,
  searchText,
  dateRange,
  isNew,
  isUnread,
  mode,
}: QueryParamsOptions): URLSearchParams {
  const queryParams = new URLSearchParams();
  queryParams.append(ComHubQueryParamsEnum.OFFSET, page.toString());
  queryParams.append(ComHubQueryParamsEnum.MAX, itemsPerPage.toString());
  if (searchText) {
    queryParams.append(ComHubQueryParamsEnum.SEARCH_TEXT, searchText);
  }
  if (dateRange?.startDate && dateRange?.endDate) {
    queryParams.append(
      ComHubQueryParamsEnum.DATE_FROM,
      dateRange.startDate.toISOString()
    );
    queryParams.append(
      ComHubQueryParamsEnum.DATE_TO,
      dateRange.endDate.toISOString()
    );
  }
  if (isNew) {
    queryParams.append(ComHubQueryParamsEnum.NEW, 'true');
  }
  if (isUnread) {
    queryParams.append(ComHubQueryParamsEnum.UNREAD, 'true');
  }
  if (mode) {
    queryParams.append(ComHubQueryParamsEnum.MODE, mode);
  }
  return queryParams;
}

/**
 * Handles optimistic updates for dismissed items in Communication Hub
 * @param queryClient - React Query client
 * @param items - Array of items to dismiss containing associatedType and associatedIds
 * @param action - 'setNewFalse' to set userState.new to false, 'removeItems' to remove items from list
 * @param rollback - If true, reverts the optimistic update (for error handling)
 */
export const handleDismissOptimisticUpdate = (
  queryClient: any,
  items: Array<{
    associatedType: UserActionAssociatedTypeEnum;
    associatedIds: number[];
  }>,
  action: 'setNewFalse' | 'removeItems',
  rollback: boolean = false
) => {
  // Get the type of item being dismissed from the first item in the array
  const dismissedType = items[0].associatedType;

  // Map the dismiss type to the corresponding query key
  const queryKeyMap: Partial<
    Record<
      UserActionAssociatedTypeEnum,
      { countKey: string[]; listKey: string[] }
    >
  > = {
    [UserActionAssociatedTypeEnum.COMPANY_RATING]: {
      countKey: [QueryKeys.ComHub.RECEIVED_REVIEWS_COUNT],
      listKey: [QueryKeys.ComHub.RECEIVED_REVIEWS],
    },
    [UserActionAssociatedTypeEnum.RATING_REQUEST]: {
      countKey: [QueryKeys.ComHub.REVIEW_REQUESTS_COUNT],
      listKey: [QueryKeys.ComHub.REVIEW_REQUESTS],
    },
    [UserActionAssociatedTypeEnum.JOB_CHAT]: {
      countKey: [QueryKeys.ComHub.JOB_CHAT_COUNT],
      listKey: [QueryKeys.ComHub.JOB_CHAT],
    },
    [UserActionAssociatedTypeEnum.DIRECT_CHAT]: {
      countKey: [QueryKeys.ComHub.DIRECT_CHAT_COUNT],
      listKey: [QueryKeys.ComHub.DIRECT_CHAT],
    },
  };

  // Update count query
  const countQueryKey = queryKeyMap[dismissedType]?.countKey;
  if (countQueryKey) {
    queryClient.setQueryData(countQueryKey, (old: any) => {
      if (old?.count) {
        // Calculate how many items are being removed
        const itemsToRemove = items.reduce(
          (total, item) => total + item.associatedIds.length,
          0
        );
        // If rollback, add back the items; otherwise subtract them
        const newCount = rollback
          ? old.count + itemsToRemove
          : Math.max(0, old.count - itemsToRemove);
        return { ...old, count: newCount };
      }
      return old;
    });
  }

  // Update list query
  const listQueryKey = queryKeyMap[dismissedType]?.listKey;
  if (listQueryKey) {
    // Get all queries with this pattern (with different searchState variations)
    const queries = queryClient.getQueriesData({
      queryKey: listQueryKey,
    });

    // Optimistically update all queries
    queries.forEach(([queryKey]: [any, any]) => {
      queryClient.setQueryData(queryKey, (old: any) => {
        if (!old) return old;

        // Handle different response structures based on the dismissed type
        switch (dismissedType) {
          case UserActionAssociatedTypeEnum.COMPANY_RATING:
            if (old?.reviews) {
              if (action === 'setNewFalse') {
                return {
                  ...old,
                  reviews: old.reviews.map((review: any) =>
                    items.some((item) =>
                      item.associatedIds.includes(review.companyRating.id)
                    )
                      ? {
                          ...review,
                          userState: {
                            ...review.userState,
                            new: rollback,
                          },
                        }
                      : review
                  ),
                };
              }
              // removeItems action
              if (rollback) {
                // For rollback, we need to restore the items
                // This is complex as we don't have the original items
                // We'll need to invalidate the query to refetch
                queryClient.invalidateQueries({ queryKey: listQueryKey });
                return old;
              }
              const filteredReviews = old.reviews.filter((review: any) => {
                const shouldKeep = !items.some((item) =>
                  item.associatedIds.includes(review.companyRating.id)
                );
                return shouldKeep;
              });
              return {
                ...old,
                reviews: filteredReviews,
              };
            }
            break;

          case UserActionAssociatedTypeEnum.RATING_REQUEST:
            if (old?.ratingRequests) {
              if (action === 'setNewFalse') {
                return {
                  ...old,
                  ratingRequests: old.ratingRequests.map((request: any) =>
                    items.some((item) =>
                      item.associatedIds.includes(request.ratingRequestId)
                    )
                      ? {
                          ...request,
                          userState: {
                            ...request.userState,
                            new: rollback,
                          },
                        }
                      : request
                  ),
                };
              }
              // removeItems action
              if (rollback) {
                queryClient.invalidateQueries({ queryKey: listQueryKey });
                return old;
              }
              return {
                ...old,
                ratingRequests: old.ratingRequests.filter(
                  (request: any) =>
                    !items.some((item) =>
                      item.associatedIds.includes(request.ratingRequestId)
                    )
                ),
              };
            }
            break;

          case UserActionAssociatedTypeEnum.JOB_CHAT:
            if (old?.jobChats) {
              if (action === 'setNewFalse') {
                return {
                  ...old,
                  jobChats: old.jobChats.map((chat: any) =>
                    items.some((item) =>
                      item.associatedIds.includes(chat.chatId)
                    )
                      ? {
                          ...chat,
                          userState: {
                            ...chat.userState,
                            new: rollback,
                          },
                        }
                      : chat
                  ),
                };
              }
              // removeItems action
              if (rollback) {
                queryClient.invalidateQueries({ queryKey: listQueryKey });
                return old;
              }
              return {
                ...old,
                jobChats: old.jobChats.filter(
                  (chat: any) =>
                    !items.some((item) =>
                      item.associatedIds.includes(chat.chatId)
                    )
                ),
              };
            }
            break;

          case UserActionAssociatedTypeEnum.DIRECT_CHAT:
            if (old?.directChats) {
              if (action === 'setNewFalse') {
                return {
                  ...old,
                  directChats: old.directChats.map((chat: any) =>
                    items.some((item) =>
                      item.associatedIds.includes(chat.chatId)
                    )
                      ? {
                          ...chat,
                          userState: {
                            ...chat.userState,
                            new: rollback,
                          },
                        }
                      : chat
                  ),
                };
              }
              // removeItems action
              if (rollback) {
                queryClient.invalidateQueries({ queryKey: listQueryKey });
                return old;
              }
              return {
                ...old,
                directChats: old.directChats.filter(
                  (chat: any) =>
                    !items.some((item) =>
                      item.associatedIds.includes(chat.chatId)
                    )
                ),
              };
            }
            break;

          default:
            break;
        }

        return old;
      });
    });
  }
};

/**
 * Handles optimistic update rollback for dismissed items
 * This is a convenience function that calls handleDismissOptimisticUpdate with rollback=true
 */
export const handleDismissOptimisticRollback = (
  queryClient: any,
  items: Array<{
    associatedType: UserActionAssociatedTypeEnum;
    associatedIds: number[];
  }>,
  action: 'setNewFalse' | 'removeItems'
) => {
  handleDismissOptimisticUpdate(queryClient, items, action, true);
};

/**
 * Invalidates queries based on the associated type of dismissed items
 * @param queryClient - React Query client
 * @param items - Array of items to dismiss containing associatedType and associatedIds
 */
export const invalidateQueriesForDismissedItems = (
  queryClient: any,
  items: Array<{
    associatedType: UserActionAssociatedTypeEnum;
    associatedIds: number[];
  }>
) => {
  // Invalidate query only for the type of items that were dismissed
  const associatedType = items[0]?.associatedType;
  if (associatedType) {
    // Map associated type to query key
    const queryKeyMap: Partial<Record<UserActionAssociatedTypeEnum, string>> = {
      [UserActionAssociatedTypeEnum.COMPANY_RATING]:
        QueryKeys.ComHub.RECEIVED_REVIEWS,
      [UserActionAssociatedTypeEnum.RATING_REQUEST]:
        QueryKeys.ComHub.REVIEW_REQUESTS,
      [UserActionAssociatedTypeEnum.JOB_CHAT]: QueryKeys.ComHub.JOB_CHAT,
      [UserActionAssociatedTypeEnum.DIRECT_CHAT]: QueryKeys.ComHub.DIRECT_CHAT,
    };

    const queryKey = queryKeyMap[associatedType];
    if (queryKey) {
      queryClient.invalidateQueries({ queryKey: [queryKey] });
      // Also invalidate company member indicators to update counters
      queryClient.invalidateQueries({
        queryKey: [QueryKeys.COMPANY_MEMBER_INDICATORS],
      });
    }
  }
};

/**
 * Creates a worker object from DirectMessage data
 * Used when opening direct message chats to provide basic worker information
 * @param message The DirectMessage object containing worker information
 * @returns A complete IWorkerData object with default values for missing fields
 */
export const createWorkerFromDirectMessage = (
  message: DirectMessage
): IWorkerData => {
  return {
    // Announcement rows have no worker peer; use 0 so header still renders display name.
    id: message.workerId ?? 0,
    fullName: message.workerFullName,
    customUrl: undefined,
    firstName: message.workerFullName.split(' ')[0] || '',
    lastName: message.workerFullName.split(' ').slice(1).join(' ') || '',
    profileImageUrl: '',
    isPremium: false,
    rating: 0,
    skills: [],
    willingToTravel: false,
    yearsInIndustry: 0,
    monthsInIndustry: 0,
    heightFeet: 0,
    heightInches: 0,
    birthday: new Date(),
    address: {
      city: '',
      country: 'UNITED_STATES',
      googleAddress: '',
      latitude: 0,
      line1: '',
      line2: '',
      longitude: 0,
      postalCode: '',
      state: { ansiAbbreviation: '', name: '', unnabreviated: '' },
      timezoneId: '',
      timezoneName: '',
    },
    cellPhone: '',
    email: '',
  };
};

/**
 * Handles optimistic updates for marking messages as read
 * @param queryClient - React Query client
 * @param chatId - The ID of the chat to mark as read
 * @param chatType - The type of chat ('job' or 'direct')
 */
export const handleMarkAsReadOptimisticUpdate = (
  queryClient: any,
  chatId: number,
  chatType: 'job' | 'direct'
) => {
  const queryKey =
    chatType === 'job'
      ? [QueryKeys.ComHub.JOB_CHAT]
      : [QueryKeys.ComHub.DIRECT_CHAT];

  const countQueryKey =
    chatType === 'job'
      ? [QueryKeys.ComHub.JOB_CHAT_COUNT]
      : [QueryKeys.ComHub.DIRECT_CHAT_COUNT];

  // Cancel any outgoing refetches
  queryClient.cancelQueries({ queryKey });
  queryClient.cancelQueries({ queryKey: countQueryKey });

  // Get all queries with this pattern (with different searchState variations)
  const queries = queryClient.getQueriesData({ queryKey });
  const countQueries = queryClient.getQueriesData({ queryKey: countQueryKey });

  const previousStates = [
    ...queries.map(([key, data]: [any, any]) => ({
      queryKey: key,
      data,
    })),
    ...countQueries.map(([key, data]: [any, any]) => ({
      queryKey: key,
      data,
    })),
  ];

  // Optimistically update all queries to set unreadMessages to 0 for the specific chat
  // For filtered lists (showOnlyNew=true), remove the item entirely
  queries.forEach(([key]: [any, any]) => {
    queryClient.setQueryData(key, (old: any) => {
      if (!old) return old;

      // Check if this is a filtered query (showOnlyNew=true)
      const isFilteredQuery = key.some(
        (k: any) => typeof k === 'boolean' && k === true
      );

      if (chatType === 'job' && old?.jobChats) {
        if (isFilteredQuery) {
          // Remove the item from filtered lists
          return {
            ...old,
            jobChats: old.jobChats.filter(
              (jobChat: any) => jobChat.chatId !== chatId
            ),
          };
        }
        // Just update the unread status for unfiltered lists
        return {
          ...old,
          jobChats: old.jobChats.map((jobChat: any) =>
            jobChat.chatId === chatId
              ? { ...jobChat, unreadMessages: 0 }
              : jobChat
          ),
        };
      }

      if (chatType === 'direct' && old?.directChats) {
        if (isFilteredQuery) {
          // Remove the item from filtered lists
          return {
            ...old,
            directChats: old.directChats.filter(
              (directChat: any) => directChat.chatId !== chatId
            ),
          };
        }
        // Just update the unread status for unfiltered lists
        return {
          ...old,
          directChats: old.directChats.map((directChat: any) =>
            directChat.chatId === chatId
              ? { ...directChat, unreadMessages: 0 }
              : directChat
          ),
        };
      }

      return old;
    });
  });

  // Optimistically update count queries (decrease by 1)
  countQueries.forEach(([key]: [any, any]) => {
    queryClient.setQueryData(key, (old: any) => {
      if (old?.count !== undefined) {
        return { ...old, count: Math.max(0, old.count - 1) };
      }
      return old;
    });
  });

  // Note: We no longer optimistically update indicators here.
  // Individual counter queries (JOB_CHAT_COUNT, DIRECT_CHAT_COUNT) are used instead.

  return { previousStates };
};

/**
 * Handles optimistic updates for marking messages as read (list only, no count)
 * @param queryClient - React Query client
 * @param chatId - The ID of the chat to mark as read
 * @param chatType - The type of chat ('job' or 'direct')
 */
export const handleMarkAsReadListOnlyOptimisticUpdate = (
  queryClient: any,
  chatId: number,
  chatType: 'job' | 'direct'
) => {
  const queryKey =
    chatType === 'job'
      ? [QueryKeys.ComHub.JOB_CHAT]
      : [QueryKeys.ComHub.DIRECT_CHAT];

  // Cancel any outgoing refetches
  queryClient.cancelQueries({ queryKey });

  // Get all queries with this pattern (with different searchState variations)
  const queries = queryClient.getQueriesData({ queryKey });

  const previousStates = queries.map(([key, data]: [any, any]) => ({
    queryKey: key,
    data,
  }));

  // Optimistically update all queries to set unreadMessages to 0 for the specific chat
  // For opening chat, we NEVER remove items from the list, just make them unbold
  queries.forEach(([key]: [any, any]) => {
    queryClient.setQueryData(key, (old: any) => {
      if (!old) return old;

      if (chatType === 'job' && old?.jobChats) {
        return {
          ...old,
          jobChats: old.jobChats.map((jobChat: any) =>
            jobChat.chatId === chatId
              ? { ...jobChat, unreadMessages: 0 }
              : jobChat
          ),
        };
      }

      if (chatType === 'direct' && old?.directChats) {
        return {
          ...old,
          directChats: old.directChats.map((directChat: any) =>
            directChat.chatId === chatId
              ? { ...directChat, unreadMessages: 0 }
              : directChat
          ),
        };
      }

      return old;
    });
  });

  return { previousStates };
};

/**
 * Handles optimistic updates for removing items from list only (no count update)
 * @param queryClient - React Query client
 * @param items - Array of items to remove containing associatedType and associatedIds
 */
export const handleRemoveFromListOnlyOptimisticUpdate = (
  queryClient: any,
  items: Array<{
    associatedType: UserActionAssociatedTypeEnum;
    associatedIds: number[];
  }>
) => {
  // Get the type of item being removed from the first item in the array
  const removedType = items[0].associatedType;

  // Map the remove type to the corresponding query key
  const queryKeyMap: Partial<Record<UserActionAssociatedTypeEnum, string[]>> = {
    [UserActionAssociatedTypeEnum.JOB_CHAT]: [QueryKeys.ComHub.JOB_CHAT],
    [UserActionAssociatedTypeEnum.DIRECT_CHAT]: [QueryKeys.ComHub.DIRECT_CHAT],
  };

  // Update list query only (no count update)
  const listQueryKey = queryKeyMap[removedType];
  if (listQueryKey) {
    // Get all queries with this pattern (with different searchState variations)
    const queries = queryClient.getQueriesData({
      queryKey: listQueryKey,
    });

    // Optimistically update all queries
    queries.forEach(([queryKey]: [any, any]) => {
      queryClient.setQueryData(queryKey, (old: any) => {
        if (!old) return old;

        // Handle different response structures based on the removed type
        switch (removedType) {
          case UserActionAssociatedTypeEnum.JOB_CHAT:
            if (old?.jobChats) {
              return {
                ...old,
                jobChats: old.jobChats.filter(
                  (chat: any) =>
                    !items.some((item) =>
                      item.associatedIds.includes(chat.chatId)
                    )
                ),
              };
            }
            break;

          case UserActionAssociatedTypeEnum.DIRECT_CHAT:
            if (old?.directChats) {
              return {
                ...old,
                directChats: old.directChats.filter(
                  (chat: any) =>
                    !items.some((item) =>
                      item.associatedIds.includes(chat.chatId)
                    )
                ),
              };
            }
            break;

          default:
            break;
        }

        return old;
      });
    });
  }
};

/**
 * Handles rollback for mark as read optimistic updates
 * @param queryClient - React Query client
 * @param previousStates - The previous states to restore
 */
export const handleMarkAsReadOptimisticRollback = (
  queryClient: any,
  previousStates: Array<{ queryKey: any; data: any }>
) => {
  previousStates.forEach(({ queryKey, data }) => {
    queryClient.setQueryData(queryKey, data);
  });
};

/**
 * Handles optimistic updates for marking messages as unread
 * @param queryClient - React Query client
 * @param chatId - The ID of the chat to mark as unread
 * @param chatType - The type of chat ('job' or 'direct')
 */
export const handleMarkAsUnreadOptimisticUpdate = (
  queryClient: any,
  chatId: number,
  chatType: 'job' | 'direct'
) => {
  const queryKey =
    chatType === 'job'
      ? [QueryKeys.ComHub.JOB_CHAT]
      : [QueryKeys.ComHub.DIRECT_CHAT];

  const countQueryKey =
    chatType === 'job'
      ? [QueryKeys.ComHub.JOB_CHAT_COUNT]
      : [QueryKeys.ComHub.DIRECT_CHAT_COUNT];

  // Cancel any outgoing refetches
  queryClient.cancelQueries({ queryKey });
  queryClient.cancelQueries({ queryKey: countQueryKey });

  // Get all queries with this pattern (with different searchState variations)
  const queries = queryClient.getQueriesData({ queryKey });
  const countQueries = queryClient.getQueriesData({ queryKey: countQueryKey });

  const previousStates = [
    ...queries.map(([key, data]: [any, any]) => ({
      queryKey: key,
      data,
    })),
    ...countQueries.map(([key, data]: [any, any]) => ({
      queryKey: key,
      data,
    })),
  ];

  // Optimistically update all queries to set unreadMessages to 1 for the specific chat
  queries.forEach(([key]: [any, any]) => {
    queryClient.setQueryData(key, (old: any) => {
      if (!old) return old;

      if (chatType === 'job' && old?.jobChats) {
        return {
          ...old,
          jobChats: old.jobChats.map((jobChat: any) =>
            jobChat.chatId === chatId
              ? { ...jobChat, unreadMessages: 1 }
              : jobChat
          ),
        };
      }

      if (chatType === 'direct' && old?.directChats) {
        return {
          ...old,
          directChats: old.directChats.map((directChat: any) =>
            directChat.chatId === chatId
              ? { ...directChat, unreadMessages: 1 }
              : directChat
          ),
        };
      }

      return old;
    });
  });

  // Optimistically update count queries (increase by 1)
  countQueries.forEach(([key]: [any, any]) => {
    queryClient.setQueryData(key, (old: any) => {
      if (old?.count !== undefined) {
        return { ...old, count: old.count + 1 };
      }
      return old;
    });
  });

  // Note: We no longer optimistically update indicators here.
  // Individual counter queries (JOB_CHAT_COUNT, DIRECT_CHAT_COUNT) are used instead.

  return { previousStates };
};

/**
 * Handles rollback for mark as unread optimistic updates
 * @param queryClient - React Query client
 * @param previousStates - The previous states to restore
 */
export const handleMarkAsUnreadOptimisticRollback = (
  queryClient: any,
  previousStates: Array<{ queryKey: any; data: any }>
) => {
  previousStates.forEach(({ queryKey, data }) => {
    queryClient.setQueryData(queryKey, data);
  });
};

/**
 * Returns the appropriate mail icon based on read status and hover state
 * @param hasUnreadMessages - Whether the message has unread messages
 * @param isHovered - Whether the icon is currently being hovered
 * @param isMobile - Whether the current device is mobile
 * @returns JSX element with the appropriate mail icon
 */
export const getMessageIcon = (
  hasUnreadMessages: boolean,
  isHovered: boolean,
  isMobile: boolean = false
) => {
  const size = isMobile ? 18 : 24;

  if (isHovered) {
    return hasUnreadMessages ? (
      <IoMailOpenOutline size={size} />
    ) : (
      <IoMailUnreadOutline size={size} />
    );
  }

  return hasUnreadMessages ? (
    <IoMailUnreadOutline size={size} />
  ) : (
    <IoMailOpenOutline size={size} />
  );
};

/**
 * Generic function to handle marking messages as read/unread
 * @param messages - Array of messages to process
 * @param markAsRead - Function to mark a single item as read
 * @param markAsUnread - Function to mark a single item as unread
 * @param getId - Function to extract the ID from a message
 * @param hasUnreadMessages - Function to check if a message has unread messages
 */
export function handleMarkMessagesAsRead<T>(
  messages: T[],
  markAsRead: (id: number, onSuccess?: () => void) => void,
  markAsUnread: (id: number, onSuccess?: () => void) => void,
  getId: (message: T) => number | undefined,
  hasUnreadMessages: (message: T) => boolean,
  onSuccess?: () => void
) {
  if (messages.length === 1) {
    const message = messages[0];
    const id = getId(message);
    if (id) {
      if (hasUnreadMessages(message)) {
        markAsRead(id, onSuccess);
      } else {
        markAsUnread(id, onSuccess);
      }
    }
  } else {
    // For multiple messages, collect IDs to process and call onSuccess after the last mutation
    const idsToProcess: number[] = [];
    messages.forEach((message) => {
      const id = getId(message);
      if (hasUnreadMessages(message) && id) {
        idsToProcess.push(id);
      }
    });
    idsToProcess.forEach((id, index) => {
      const isLast = index === idsToProcess.length - 1;
      markAsRead(id, isLast ? onSuccess : undefined);
    });
  }
}

/**
 * Optimized batch function to handle marking multiple messages as read
 * This reduces API calls and query invalidations by batching operations
 * For "Mark All as Read" functionality - only marks unread messages as read
 * @param messages - Array of messages to process
 * @param batchMarkAsRead - Function to mark multiple items as read
 * @param markAsRead - Function to mark a single item as read (fallback)
 * @param markAsUnread - Function to mark a single item as unread (for single message toggle)
 * @param getId - Function to extract the ID from a message
 * @param hasUnreadMessages - Function to check if a message has unread messages
 * @param onSuccess - Optional callback invoked when the mutation succeeds
 */
export function handleBatchMarkJobChatsAsRead<T>(
  messages: T[],
  batchMarkAsRead: (ids: number[], onSuccess?: () => void) => void,
  markAsRead: (id: number, onSuccess?: () => void) => void,
  markAsUnread: (id: number, onSuccess?: () => void) => void,
  getId: (message: T) => number | undefined,
  hasUnreadMessages: (message: T) => boolean,
  onSuccess?: () => void
) {
  if (messages.length === 1) {
    // Single message - toggle read/unread
    const message = messages[0];
    const id = getId(message);
    if (id) {
      if (hasUnreadMessages(message)) {
        markAsRead(id, onSuccess);
      } else {
        markAsUnread(id, onSuccess);
      }
    }
  } else {
    // Multiple messages - only mark unread ones as read (Mark All as Read)
    const idsToRead: number[] = [];

    messages.forEach((message) => {
      const id = getId(message);
      if (id && hasUnreadMessages(message)) {
        idsToRead.push(id);
      }
    });

    if (idsToRead.length > 0) {
      batchMarkAsRead(idsToRead, onSuccess);
    }
  }
}

export const isMessagingTab = (tabKey: string): boolean => {
  return (
    tabKey === ComHubTabKeys.JOB_MESSAGES ||
    tabKey === ComHubTabKeys.DIRECT_MESSAGES
  );
};

/**
 * Determines the appropriate query parameter (NEW or UNREAD) based on tab type
 * @param tabKey - The tab key to check
 * @returns The appropriate query parameter enum value
 */
export const getTabQueryParameter = (tabKey: string): ComHubQueryParamsEnum => {
  return isMessagingTab(tabKey)
    ? ComHubQueryParamsEnum.UNREAD
    : ComHubQueryParamsEnum.NEW;
};

/**
 * Calculates the delta for counter updates when marking messages as read/unread
 * - Single message: toggles between read/unread (+1 if read→unread, -1 if unread→read)
 * - Multiple messages: only unread messages are marked as read (returns -unreadCount)
 * @param messages - Array of messages to process
 * @param isUnread - Function to check if a message has unread messages
 * @returns The delta value (negative = decrease counter, positive = increase counter)
 */
export function calculateReadUnreadDelta<T>(
  messages: T[],
  isUnread: (message: T) => boolean
): number {
  if (messages.length === 1) {
    return isUnread(messages[0]) ? -1 : 1;
  }
  // Multiple messages: only unread messages are marked as read
  return -messages.filter(isUnread).length;
}
